/**
 * ============================================================
 * EDEL CRM — Design Tokens
 * ============================================================
 *
 * edel-app (src/lib/tokens.ts v3.0.0) tasarım sisteminin web/CRM
 * tarafına taşınmış hâli. "Premium derinlik & sıcaklık" dili:
 *   - Elevation hiyerarşisi: bg < surface < surface2
 *   - gradientHero (ember) → özet/metrik kartları
 *   - Yumuşak gölge + renkli glow → derinlik ve enerji
 *
 * Hardcode renk YASAK: tüm renkler buradan veya AntD token'ından gelir.
 * Bu palet hem AntD ConfigProvider'ı (src/theme/antd.ts) hem de
 * CSS değişkenlerini (src/styles/theme.css) besler.
 *
 * ⚠️ edel-app/src/lib/tokens.ts ile senkron tutun.
 */

export type ThemeMode = "light" | "dark";

export interface EdelPalette {
  // ── Elevation / yüzeyler ──────────────────────────────
  bg: string; // sayfa zemini (AntD colorBgLayout)
  surface: string; // standart kart (AntD colorBgContainer)
  surface2: string; // inset / tablo başlığı / hover / kanban gövde
  surface3: string; // sıcak vurgu yüzeyi
  cardSurface: string; // öne çıkan kart
  elevated: string; // float eden yüzey: modal / dropdown / drawer (colorBgElevated)

  // ── Metin ─────────────────────────────────────────────
  text: string;
  textMuted: string;
  textSoft: string;

  // ── Marka (turuncu) ───────────────────────────────────
  primary: string;
  primaryLight: string;
  primaryDark: string;
  primarySoft: string;
  primaryMuted: string;

  // ── Accent (kırmızı) ──────────────────────────────────
  accent: string;
  accentHover: string;
  accentSoft: string;

  // ── Durum renkleri ────────────────────────────────────
  success: string;
  successSoft: string;
  warning: string;
  warningSoft: string;
  error: string;
  errorSoft: string;
  info: string;
  infoSoft: string;

  // ── Kenarlıklar ───────────────────────────────────────
  border: string;
  borderLight: string;
  borderStrong: string;
  borderPrimary: string;

  // ── Özel ──────────────────────────────────────────────
  ring: string; // focus / aktif kontrol halkası
  scrollThumb: string;
  scrollThumbHover: string;

  // ── Gradientler (köşeden köşeye renk dizileri) ────────
  gradientHero: readonly [string, string, string];
  gradientPrimary: readonly [string, string, string];
  gradientInfo: readonly [string, string, string];
  gradientSuccess: readonly [string, string, string];

  // ── Gölgeler (CSS box-shadow) ─────────────────────────
  shadowXs: string;
  shadowSm: string;
  shadowMd: string;
  shadowLg: string;
  shadowXl: string;

  // ── Renkli glow'lar (CTA & durum vurgusu) ─────────────
  glowPrimary: string;
  glowAccent: string;
  glowSuccess: string;
  glowInfo: string;
}

// ============================================================
// LIGHT
// ============================================================
export const lightPalette: EdelPalette = {
  bg: "#fffbf5",
  surface: "#ffffff",
  surface2: "#fdf6ec",
  surface3: "#fff7ed",
  cardSurface: "#ffffff",
  elevated: "#ffffff",

  text: "#0f172a",
  textMuted: "#475569",
  textSoft: "#94a3b8",

  primary: "#ea580c",
  primaryLight: "#f97316",
  primaryDark: "#c2410c",
  primarySoft: "#ffedd5",
  primaryMuted: "#fed7aa",

  accent: "#ef4444",
  accentHover: "#dc2626",
  accentSoft: "#fee2e2",

  success: "#16a34a",
  successSoft: "#dcfce7",
  warning: "#f59e0b",
  warningSoft: "#fef3c7",
  error: "#ef4444",
  errorSoft: "#fee2e2",
  info: "#3b82f6",
  infoSoft: "#dbeafe",

  border: "#ebdccf",
  borderLight: "#f3eee7",
  borderStrong: "#d1c4b5",
  borderPrimary: "#fdba74",

  ring: "rgba(234, 88, 12, 0.22)",
  scrollThumb: "rgba(194, 65, 12, 0.22)",
  scrollThumbHover: "rgba(194, 65, 12, 0.38)",

  gradientHero: ["#c2410c", "#ea580c", "#f97316"],
  gradientPrimary: ["#9a3412", "#ea580c", "#fb923c"],
  gradientInfo: ["#1e3a5f", "#2563eb", "#60a5fa"],
  gradientSuccess: ["#14532d", "#16a34a", "#4ade80"],

  shadowXs: "0 1px 2px rgba(15, 23, 42, 0.04)",
  shadowSm: "0 1px 3px rgba(15, 23, 42, 0.05), 0 1px 2px rgba(15, 23, 42, 0.04)",
  shadowMd: "0 2px 8px rgba(15, 23, 42, 0.06), 0 1px 3px rgba(15, 23, 42, 0.04)",
  shadowLg: "0 8px 24px rgba(15, 23, 42, 0.08), 0 2px 8px rgba(15, 23, 42, 0.05)",
  shadowXl: "0 16px 48px rgba(15, 23, 42, 0.12), 0 6px 16px rgba(15, 23, 42, 0.06)",

  glowPrimary: "0 4px 16px rgba(234, 88, 12, 0.28)",
  glowAccent: "0 4px 16px rgba(239, 68, 68, 0.25)",
  glowSuccess: "0 4px 16px rgba(22, 163, 74, 0.22)",
  glowInfo: "0 4px 16px rgba(37, 99, 235, 0.22)",
};

// ============================================================
// DARK
// ============================================================
export const darkPalette: EdelPalette = {
  bg: "#0f172a",
  surface: "#1e293b",
  surface2: "#334155",
  surface3: "#431407",
  cardSurface: "#263347",
  elevated: "#263347",

  text: "#f8fafc",
  textMuted: "#94a3b8",
  textSoft: "#64748b",

  primary: "#f97316",
  primaryLight: "#fb923c",
  primaryDark: "#ea580c",
  primarySoft: "#7c2d12",
  primaryMuted: "#9a3412",

  accent: "#f87171",
  accentHover: "#ef4444",
  accentSoft: "#7f1d1d",

  success: "#22c55e",
  successSoft: "#14532d",
  warning: "#fbbf24",
  warningSoft: "#78350f",
  error: "#f87171",
  errorSoft: "#7f1d1d",
  info: "#60a5fa",
  infoSoft: "#1e3a8a",

  border: "#334155",
  borderLight: "#283549",
  borderStrong: "#475569",
  borderPrimary: "#9a3412",

  ring: "rgba(249, 115, 22, 0.35)",
  scrollThumb: "rgba(148, 163, 184, 0.25)",
  scrollThumbHover: "rgba(148, 163, 184, 0.45)",

  gradientHero: ["#431407", "#7c2d12", "#9a3412"],
  gradientPrimary: ["#7c2d12", "#ea580c", "#fb923c"],
  gradientInfo: ["#1e3a5f", "#2563eb", "#60a5fa"],
  gradientSuccess: ["#14532d", "#16a34a", "#4ade80"],

  shadowXs: "0 1px 2px rgba(0, 0, 0, 0.25)",
  shadowSm: "0 1px 3px rgba(0, 0, 0, 0.3)",
  shadowMd: "0 2px 8px rgba(0, 0, 0, 0.35), 0 1px 3px rgba(0, 0, 0, 0.3)",
  shadowLg: "0 8px 24px rgba(0, 0, 0, 0.45), 0 2px 8px rgba(0, 0, 0, 0.3)",
  shadowXl: "0 16px 48px rgba(0, 0, 0, 0.55), 0 6px 16px rgba(0, 0, 0, 0.35)",

  glowPrimary: "0 4px 18px rgba(249, 115, 22, 0.38)",
  glowAccent: "0 4px 18px rgba(248, 113, 113, 0.32)",
  glowSuccess: "0 4px 18px rgba(34, 197, 94, 0.30)",
  glowInfo: "0 4px 18px rgba(96, 165, 250, 0.30)",
};

export const getPalette = (mode: ThemeMode): EdelPalette =>
  mode === "dark" ? darkPalette : lightPalette;

// ============================================================
// STATIK ÖLÇEKLER (moda bağımsız)
// ============================================================
export const radius = {
  sm: 8,
  md: 12,
  lg: 16,
  xl: 20,
  card: 16,
  button: 10,
  input: 10,
  pill: 999,
} as const;

export const space = {
  xs: 4,
  sm: 8,
  md: 12,
  lg: 16,
  xl: 24,
  xxl: 32,
} as const;

/** Tip ölçeği (px). Yeni bileşenler yalnız bu değerleri kullanır. */
export const fontSize = {
  caption: 12,
  small: 13,
  body: 14,
  lead: 16,
  title: 20,
  display: 24,
} as const;

export const fontFamily =
  "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', sans-serif";

// ============================================================
// HERO OVERLAY (gradientHero kartlarında derinlik)
// Alt koyu scrim (metin oturması) + üst kısık sheen (yumuşak ışık)
// ============================================================
export const heroOverlay = {
  scrim: "linear-gradient(to bottom, rgba(0,0,0,0) 10%, rgba(0,0,0,0.24) 100%)",
  sheen: "linear-gradient(to bottom, rgba(255,255,255,0.16) 0%, rgba(255,255,255,0) 55%)",
  border: "1px solid rgba(255,255,255,0.10)",
} as const;

/** gradientHero dizisini 135° CSS linear-gradient'e çevirir. */
export const heroGradientCss = (palette: EdelPalette): string =>
  `linear-gradient(135deg, ${palette.gradientHero.join(", ")})`;

/** Bir gradient dizisini 135° CSS linear-gradient'e çevirir. */
export const gradientCss = (colors: readonly string[]): string =>
  `linear-gradient(135deg, ${colors.join(", ")})`;

// ============================================================
// CSS DEĞİŞKENLERİ
// theme.css'in (scrollbar, hover, focus) okuyacağı custom property'ler.
// body[data-theme] üstüne yazılır.
// ============================================================
export const cssVars = (p: EdelPalette): Record<string, string> => ({
  "--edel-bg": p.bg,
  "--edel-surface": p.surface,
  "--edel-surface-2": p.surface2,
  "--edel-surface-3": p.surface3,
  "--edel-card-surface": p.cardSurface,
  "--edel-elevated": p.elevated,
  "--edel-text": p.text,
  "--edel-text-muted": p.textMuted,
  "--edel-text-soft": p.textSoft,
  "--edel-primary": p.primary,
  "--edel-primary-light": p.primaryLight,
  "--edel-primary-dark": p.primaryDark,
  "--edel-primary-soft": p.primarySoft,
  "--edel-primary-muted": p.primaryMuted,
  "--edel-accent": p.accent,
  "--edel-success": p.success,
  "--edel-success-soft": p.successSoft,
  "--edel-warning": p.warning,
  "--edel-warning-soft": p.warningSoft,
  "--edel-error": p.error,
  "--edel-error-soft": p.errorSoft,
  "--edel-info": p.info,
  "--edel-info-soft": p.infoSoft,
  "--edel-border": p.border,
  "--edel-border-light": p.borderLight,
  "--edel-border-strong": p.borderStrong,
  "--edel-border-primary": p.borderPrimary,
  "--edel-ring": p.ring,
  "--edel-scroll-thumb": p.scrollThumb,
  "--edel-scroll-thumb-hover": p.scrollThumbHover,
  "--edel-shadow-sm": p.shadowSm,
  "--edel-shadow-md": p.shadowMd,
  "--edel-shadow-lg": p.shadowLg,
  "--edel-shadow-xl": p.shadowXl,
  "--edel-glow-primary": p.glowPrimary,
  "--edel-gradient-hero": heroGradientCss(p),
});
